import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Foundations() {
  const [recipes, setRecipes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    client.get("/content/recipes?preview=true").then(({ data }) => {
      setRecipes(data.recipes.filter((r) => r.chapter === "foundations"));
    }).catch(() => navigate("/login"));
  }, [navigate]);

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        <Link to="/read" data-testid="back-reader" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← Table of Contents</Link>
      </div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pb-12">
        <div className="overline text-mizizi-gold">Foundations</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-[0.95] text-mizizi-charcoal">Five sauces. One table.</h1>
        <p className="mt-6 text-lg text-mizizi-charcoal/70 max-w-2xl leading-relaxed">
          These are the small-batch preparations that turn a Sunday into a Mizizi Sunday. Master any two — the whole book bends toward you.
        </p>
      </header>

      <Divider />

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {recipes.map((r, i) => (
            <Link
              key={r.slug}
              to={`/read/recipe/${r.slug}`}
              data-testid={`foundation-${r.slug}`}
              className="block group border border-mizizi-gold/30 hover:border-mizizi-gold transition-colors overflow-hidden"
            >
              <div className="aspect-[3/2] overflow-hidden bg-mizizi-forest">
                <img
                  src={`${API_BASE}/api/images/brand/${r.slug}`}
                  alt={r.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                />
              </div>
              <div className="p-6">
                <div className="overline text-mizizi-gold">{String(i + 1).padStart(2, "0")} · Sauce</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-2 text-mizizi-charcoal leading-tight">{r.title}</h3>
                <p className="mt-3 text-sm text-mizizi-charcoal/70 italic leading-snug line-clamp-2">{r.story}</p>
                <div className="mt-5 flex items-center justify-between">
                  <div className="overline text-mizizi-charcoal/50">{r.prep_min + r.cook_min} min · Serves {r.servings}</div>
                  <div className="overline text-mizizi-terracotta group-hover:text-mizizi-gold">Read →</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-mizizi-forest text-mizizi-cream py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-mizizi-gold">The rule of the pantry</div>
          <p className="font-serif text-2xl md:text-3xl mt-4 leading-snug italic">
            "One sauce made ahead makes three weeknights easy. Two sauces made ahead means the neighbours are staying for dinner."
          </p>
        </div>
      </section>
    </div>
  );
}
